import apiClient from './apiClient';

// Define interface for API response
interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

// Define subscription plan interface
export interface SubscriptionPlan {
  _id: string;
  name: string;
  description: string;
  features: string[];
  interviewMinutes: number;
  price: number;
  discountPercentage: number;
  planType: string;
  isRecurring: boolean;
  isPurchasable?: boolean;
  isActive: boolean;
  isAddon: boolean;
  isUnlimited: boolean;
  numberOfInterviews?: number;
  currentPrice: number;
  createdAt: string;
  updatedAt: string;
}

// Define user subscription response interface
export interface UserSubscription {
  _id: string;
  user: string;
  plan: SubscriptionPlan;
  startDate: string;
  isRecurring: boolean;
  endDate: string;
  minutesLeft: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Define coupon validation response interface
export interface CouponValidationResult {
  isValid: boolean;
  couponId?: string;
  code?: string;
  couponType?: 'percentage' | 'fixed_amount' | 'free_minutes';
  value?: number;
  discountAmount: number;
  freeMinutes: number;
  originalPrice: number;
  discountedPrice: number;
  expiresAt?: string;
  message: string;
  planId?: string;
  // Additional properties for detailed coupon information
  startDate?: string;
  description?: string;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  applicablePlans?: string[];
  totalUsageLimit?: number;
  perUserLimit?: number;
  usageCount?: number;
  requiresExistingSubscription?: boolean;
  razorpayOfferId?: string;
  // Add support for multiple plan discount calculations, this will not be returned from the API
  planDiscounts?: Array<{
    planId: string;
    discountedPrice: number;
    originalPrice: number;
    discountAmount: number;
    freeMinutes: number;
  }>;
}

export interface CancellationReason {
  TOO_EXPENSIVE: 'too_expensive';
  NOT_USING_ENOUGH: 'not_using_enough';
  POOR_QUALITY: 'poor_quality';
  MISSING_FEATURES: 'missing_features';
  FOUND_ALTERNATIVE: 'found_alternative';
  TECHNICAL_ISSUES: 'technical_issues';
  TEMPORARY_PAUSE: 'temporary_pause';
  POOR_SUPPORT: 'poor_support';
  OTHER: 'other';
}

export const CANCELLATION_REASONS = {
  TOO_EXPENSIVE: 'too_expensive',
  NOT_USING_ENOUGH: 'not_using_enough',
  POOR_QUALITY: 'poor_quality',
  MISSING_FEATURES: 'missing_features',
  FOUND_ALTERNATIVE: 'found_alternative',
  TECHNICAL_ISSUES: 'technical_issues',
  TEMPORARY_PAUSE: 'temporary_pause',
  POOR_SUPPORT: 'poor_support',
  OTHER: 'other'
} as const;

export type CancellationReasonType = typeof CANCELLATION_REASONS[keyof typeof CANCELLATION_REASONS];

export interface CancelSubscriptionRequest {
  reasons: CancellationReasonType[];
  otherReason?: string;
  feedbackOptional?: string;
}

export interface CancellationRecord {
  _id: string;
  user: string;
  plan: string;
  subscription?: string;
  reasons: CancellationReasonType[];
  otherReason?: string;
  cancelledAt: string;
  razorpaySubscriptionId?: string;
  isRecurring: boolean;
  planDetails?: {
    name: string;
    price: number;
    interviewMinutes: number;
    recurringInterval?: string;
  };
  metadata?: {
    minutesLeft?: number;
    daysUsed?: number;
    totalDaysInPlan?: number;
    lastUsedAt?: string;
    feedbackOptional?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Helper function to extract data from API response
function extractDataFromResponse<T>(response: any, entityName: string): T[] {
  console.log(`SubscriptionService: ${entityName} raw response:`, response);
  
  try {
    // Case 1: If it's already an array, use it directly
    if (Array.isArray(response)) {
      console.log(`SubscriptionService: ${entityName} is already an array with ${response.length} items`);
      return response;
    }
    
    // Case 2: Handle normalized response from apiClient (numeric keys with _response metadata)
    if (response && typeof response === 'object' && '_response' in response) {
      console.log(`SubscriptionService: ${entityName} is in normalized format with _response property`);
      
      // Collect all items with numeric keys
      const items: T[] = [];
      for (const key in response) {
        // Skip non-numeric keys and _response
        if (key === '_response' || isNaN(Number(key))) {
          continue;
        }
        
        items.push(response[key] as T);
      }
      
      console.log(`SubscriptionService: ${entityName} extracted ${items.length} items from normalized response`);
      return items;
    }
    
    // Case 3: If it has a nested data property that's an array
    if (response && response.data && Array.isArray(response.data)) {
      console.log(`SubscriptionService: ${entityName} extracted from response.data with ${response.data.length} items`);
      return response.data;
    }
    
    // Case 4: If it's an object with a nested structure { statusCode, message, data }
    if (response && typeof response === 'object' && 'statusCode' in response && response.data) {
      if (Array.isArray(response.data)) {
        console.log(`SubscriptionService: ${entityName} extracted from ApiResponse.data with ${response.data.length} items`);
        return response.data;
      }
      
      // If response.data is an object with another data property (double nesting)
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log(`SubscriptionService: ${entityName} extracted from double-nested data with ${response.data.data.length} items`);
        return response.data.data;
      }
    }
    
    // Log detailed structure if we couldn't extract data
    console.warn(`SubscriptionService: Unexpected ${entityName} response format:`, response);
    console.warn(`SubscriptionService: Response type: ${typeof response}`);
    if (response && typeof response === 'object') {
      console.warn(`SubscriptionService: Response keys: ${Object.keys(response).join(', ')}`);
      if (response.data) {
        console.warn(`SubscriptionService: response.data type: ${typeof response.data}`);
        console.warn(`SubscriptionService: response.data keys: ${Object.keys(response.data || {}).join(', ')}`);
      }
    }
    
    // Return empty array as fallback
    return [];
  } catch (error) {
    console.error(`SubscriptionService: Error processing ${entityName} response:`, error);
    return [];
  }
}

// Subscription service class
class SubscriptionService {
  // Cache storage
  private plansCache: SubscriptionPlan[] | null = null;
  
  // Pending promises to prevent duplicate in-flight requests
  private plansPromise: Promise<SubscriptionPlan[]> | null = null;
  
  // Cache timeout - 30 minutes
  private cacheTimeout = 30 * 60 * 1000;
  private lastCacheTime: Record<string, number> = {};
  
  // Add new properties for user subscription
  private userSubscriptionCache: UserSubscription | null = null;
  private userSubscriptionPromise: Promise<UserSubscription | null> | null = null;
  private lastUserSubscriptionCacheTime = 0;
  
  // Add coupon validation cache
  private couponValidationCache: Record<string, { timestamp: number, result: CouponValidationResult }> = {};
  private couponValidationCacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout for coupons
  
  // Fetch subscription plans
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const cacheKey = 'subscriptionPlans';
    // Check if cache has expired
    const now = Date.now();
    if (this.lastCacheTime[cacheKey] && now - this.lastCacheTime[cacheKey] > this.cacheTimeout) {
      console.log('SubscriptionService: Subscription plans cache expired, clearing');
      this.plansCache = null;
    }
    
    // Return cached data if available
    if (this.plansCache) {
      console.log(`SubscriptionService: Returning cached subscription plans (${this.plansCache.length} items)`);
      return this.plansCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.plansPromise) {
      console.log('SubscriptionService: Returning pending subscription plans request');
      return this.plansPromise;
    }
    
    // Otherwise, make a new request
    console.log('SubscriptionService: Fetching subscription plans from API...');
    this.plansPromise = this.fetchSubscriptionPlans();
    
    try {
      const data = await this.plansPromise;
      
      // If API returned no plans, use fallback data
      if (data.length === 0) {
        console.log('SubscriptionService: API returned no plans, using fallback data');
        this.plansCache = null;
        this.lastCacheTime[cacheKey] = Date.now();
        return [];
      }
      
      // Store result in cache
      this.plansCache = data;
      this.lastCacheTime[cacheKey] = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.plansPromise = null;
    }
  }
  
  private async fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      console.log('SubscriptionService: Making API request to /subscriptions/plans...');
      const response = await apiClient.get<ApiResponse<SubscriptionPlan[]>>('/subscriptions/plans');
      console.log('SubscriptionService: Raw API response received:', response);
      
      // Log complete response details
      if (response && typeof response === 'object') {
        if ('_response' in response) {
          console.log('SubscriptionService: Response contains _response metadata:', response._response);
          
          // Log all numeric keys
          const numericKeys = Object.keys(response).filter(key => !isNaN(Number(key)));
          console.log(`SubscriptionService: Response contains ${numericKeys.length} items with numeric keys:`, numericKeys);
        }
      }
      
      const plans = extractDataFromResponse<SubscriptionPlan>(response, 'subscription plans');
      console.log('SubscriptionService: Final extracted plans:', plans);
      return plans;
    } catch (error) {
      console.error('SubscriptionService: Failed to fetch subscription plans:', error);
      return [];
    }
  }
  
  // Method to clear the cache if needed (e.g., for forced refresh)
  clearCache() {
    this.plansCache = null;
    this.lastCacheTime = {};
    console.log('SubscriptionService: Cache cleared');
  }
  
  // Method to get subscription plans by type (regular plans or add-ons)
  async getSubscriptionPlansByType(isAddon: boolean): Promise<SubscriptionPlan[]> {
    const plans = await this.getSubscriptionPlans();
    return plans.filter(plan => plan.isAddon === isAddon);
  }
  
  // Get current user's subscription
  async getUserSubscription(forceRefresh = false): Promise<UserSubscription | null> {
    // Check if cache has expired (after 5 minutes)
    const now = Date.now();
    const cacheExpired = now - this.lastUserSubscriptionCacheTime > 5 * 60 * 1000;
    
    // Return cached data if available and not forcing refresh
    if (!forceRefresh && !cacheExpired && this.userSubscriptionCache) {
      console.log('SubscriptionService: Returning cached user subscription');
      return this.userSubscriptionCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.userSubscriptionPromise) {
      console.log('SubscriptionService: Returning pending user subscription request');
      return this.userSubscriptionPromise;
    }
    
    // Otherwise, make a new request
    console.log('SubscriptionService: Fetching user subscription from API...');
    this.userSubscriptionPromise = this.fetchUserSubscription();
    
    try {
      const data = await this.userSubscriptionPromise;
      
      // Store result in cache
      this.userSubscriptionCache = data;
      this.lastUserSubscriptionCacheTime = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.userSubscriptionPromise = null;
    }
  }
  
  private async fetchUserSubscription(): Promise<UserSubscription | null> {
    try {
      console.log('SubscriptionService: Making API request to /subscriptions/user/me...');
      const response = await apiClient.get<ApiResponse<UserSubscription>>('/subscriptions/user/me');
      console.log('SubscriptionService: Raw user subscription response:', response);
      
      // Extract user subscription data
      let subscription: UserSubscription | null = null;
    
      if (response && typeof response === 'object') {
        // Case 1: Direct response with _response metadata (most common for free plans)
        if ('_id' in response && 'user' in response && '_response' in response) {
          console.log('SubscriptionService: Found subscription directly in root response object');
          
          // The response is directly the subscription, just need to handle plan if missing
          const subData = {...response} as any;
          
          // Remove _response from the subscription data
          delete subData._response;
          
          // Check if plan is missing (common for free plans)
          if (!subData.plan) {
            console.log('SubscriptionService: No plan found in subscription (likely a free plan)');
            // Create a default free plan object
            subData.plan = {
              _id: 'free',
              name: 'Free Plan',
              description: 'Basic free plan',
              features: ['Limited minutes'],
              interviewMinutes: subData.minutesLeft || 0,
              price: 0,
              discountPercentage: 0,
              planType: 'free',
              isRecurring: false,
              isAddon: false,
              currentPrice: 0
            };
          }
          
          subscription = subData as UserSubscription;
        }
        // Case 2: Check if response is directly in the data property
        else if ('data' in response) {
          const responseData = response.data as any; // Use any for type flexibility during inspection
          if (responseData && typeof responseData === 'object') {
            // Check if data contains a direct subscription object with _id, plan, etc.
            if ('_id' in responseData && 'user' in responseData) {
              console.log('SubscriptionService: Found subscription directly in response.data');
              
              // Handle missing plan for free subscriptions
              if (!responseData.plan) {
                responseData.plan = {
                  _id: 'free',
                  name: 'Free Plan',
                  description: 'Basic free plan',
                  features: ['Limited minutes'],
                  interviewMinutes: responseData.minutesLeft || 0,
                  price: 0,
                  discountPercentage: 0,
                  planType: 'free',
                  isRecurring: false,
                  isAddon: false,
                  currentPrice: 0
                };
              }
              
              subscription = responseData as UserSubscription;
            } 
            // Check if data contains a nested data property (API wrapper format)
            else if ('data' in responseData && responseData.data && typeof responseData.data === 'object') {
              console.log('SubscriptionService: Found subscription in nested response.data.data');
              if ('_id' in responseData.data && 'user' in responseData.data) {
                
                // Handle missing plan for free subscriptions
                if (!responseData.data.plan) {
                  responseData.data.plan = {
                    _id: 'free',
                    name: 'Free Plan',
                    description: 'Basic free plan',
                    features: ['Limited minutes'],
                    interviewMinutes: responseData.data.minutesLeft || 0,
                    price: 0,
                    discountPercentage: 0,
                    planType: 'free',
                    isRecurring: false,
                    isAddon: false,
                    currentPrice: 0
                  };
                }
                
                subscription = responseData.data as UserSubscription;
              }
            }
          }
        } 
        // Case 3: Check normalized format from apiClient with numeric keys
        else if ('_response' in response) {
          console.log('SubscriptionService: Response is in normalized format with _response property');
          
          // Normalized format from apiClient - Check for numeric keys
          const numericKeys = Object.keys(response).filter(key => !isNaN(Number(key)));
          if (numericKeys.length > 0) {
            console.log(`SubscriptionService: Found ${numericKeys.length} numeric keys in response`);
            // Use type assertion to avoid TypeScript error
            const responseObj = response as Record<string, any>;
            const firstKey = numericKeys[0];
            if (responseObj[firstKey]) {
              const subData = responseObj[firstKey];
              
              // Handle missing plan for free subscriptions
              if (!subData.plan) {
                subData.plan = {
                  _id: 'free',
                  name: 'Free Plan',
                  description: 'Basic free plan',
                  features: ['Limited minutes'],
                  interviewMinutes: subData.minutesLeft || 0,
                  price: 0,
                  discountPercentage: 0,
                  planType: 'free',
                  isRecurring: false,
                  isAddon: false,
                  currentPrice: 0
                };
              }
              
              subscription = subData as UserSubscription;
            }
          }
        }
      }
      
      // Log subscription details to help debug
      if (subscription) {
        console.log('SubscriptionService: Extracted user subscription successfully:', {
          id: subscription._id,
          planName: subscription.plan?.name || 'Free Plan',
          planType: subscription.plan?.planType || 'free',
          isActive: subscription.isActive,
          minutesLeft: subscription.minutesLeft,
          endDate: subscription.endDate
        });
      } else {
        console.warn('SubscriptionService: Failed to extract subscription from response');
        console.warn('SubscriptionService: Response structure:', JSON.stringify(response, null, 2));
      }
      
      return subscription;
    } catch (error) {
      console.error('SubscriptionService: Failed to fetch user subscription:', error);
      return null;
    }
  }
  
  // Get user's subscription plan type (free, monthly, yearly, quarterly)
  async getUserPlanType(): Promise<'free' | 'monthly' | 'yearly' | 'quarterly'> {
    const subscription = await this.getUserSubscription();
    
    if (!subscription || !subscription.isActive) {
      return 'free';
    }
    
    return subscription.plan.planType as 'monthly' | 'yearly' | 'quarterly';
  }
  
  // Get remaining minutes
  async getRemainingMinutes(): Promise<number> {
    const subscription = await this.getUserSubscription();
    return subscription?.minutesLeft || 0;
  }
  
  // Format subscription expiry date
  formatExpiryDate(endDate: string): string {
    const date = new Date(endDate);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  }
  
  // Get formatted subscription info for UI
  async getSubscriptionInfo(): Promise<{
    planType: 'free' | 'monthly' | 'yearly' | 'quarterly';
    planName: string;
    isActive: boolean;
    isRecurring: boolean;
    expiryDate: string;
    minutesLeft: number;
    isUnlimited: boolean;
  }> {
    const subscription = await this.getUserSubscription(true); // Force refresh to ensure latest data
    console.log('SubscriptionService: getSubscriptionInfo - fetched subscription:', subscription);
    
    if (!subscription) {
      console.log('SubscriptionService: No subscription found, returning free plan info');
      return {
        planType: 'free',
        planName: 'Free Plan',
        isActive: false,
        isRecurring: false,
        expiryDate: '',
        minutesLeft: 0,
        isUnlimited: false
      };
    }
    
    const planType = subscription.plan?.planType as 'monthly' | 'yearly' | 'quarterly';
    console.log('SubscriptionService: Returning active subscription info:', {
      planType,
      planName: subscription.plan?.name,
      isActive: subscription.isActive,
      endDate: subscription.endDate,
      minutesLeft: subscription.minutesLeft,
      isUnlimited: subscription.plan?.isUnlimited || false
    });
    
    return {
      planType: planType,
      planName: subscription.plan?.name || 'Unknown Plan',
      isActive: subscription.isActive,
      isRecurring: subscription.isRecurring,
      expiryDate: this.formatExpiryDate(subscription.endDate),
      minutesLeft: subscription.minutesLeft,
      isUnlimited: subscription.plan?.isUnlimited || false
    };
  }
  
  // Purchase a new subscription plan
  async purchasePlan(planId: string, startDate: string, endDate: string, minutes: number): Promise<UserSubscription | null> {
    try {
      console.log('SubscriptionService: Purchasing plan...', { planId, startDate, endDate, minutes });
      
      const payload = {
        plan: planId,
        startDate,
        endDate,
        minutesLeft: minutes
      };
      
      const response = await apiClient.post<ApiResponse<UserSubscription>>('/subscriptions/user', payload);
      console.log('SubscriptionService: Purchase plan response:', response);
      
      // Clear the subscription cache to ensure fresh data on next fetch
      this.userSubscriptionCache = null;
      this.lastUserSubscriptionCacheTime = 0;
      
      // Extract user subscription data from response
      let subscription: UserSubscription | null = null;
      
      if (response && typeof response === 'object') {
        if ('_response' in response) {
          // The response itself might be the subscription object with _response metadata
          if ('_id' in response && 'plan' in response && 'isActive' in response) {
            // This is directly the subscription object
            subscription = response as unknown as UserSubscription;
          } else {
            // Look for the subscription in numbered keys
            const numericKeys = Object.keys(response).filter(key => !isNaN(Number(key)));
            if (numericKeys.length > 0) {
              const responseObj = response as Record<string, any>;
              const firstKey = numericKeys[0];
              if (responseObj[firstKey]) {
                subscription = responseObj[firstKey] as UserSubscription;
              }
            }
          }
        } else if ('data' in response && response.data) {
          // Direct API format
          subscription = response.data as UserSubscription;
        }
      }
      
      console.log('SubscriptionService: Extracted subscription data:', subscription);
      return subscription;
    } catch (error) {
      console.error('SubscriptionService: Failed to purchase plan:', error);
      throw error;
    }
  }
  
  // Upgrade existing subscription plan
  async upgradePlan(planId: string, startDate: string, endDate: string, minutes: number): Promise<UserSubscription | null> {
    try {
      console.log('SubscriptionService: Upgrading plan...', { planId, startDate, endDate, minutes });
      
      const payload = {
        plan: planId,
        startDate,
        endDate,
        minutesLeft: minutes
      };
      
      const response = await apiClient.patch<ApiResponse<UserSubscription>>('/subscriptions/user', payload);
      console.log('SubscriptionService: Upgrade plan response:', response);
      
      // Clear the subscription cache to ensure fresh data on next fetch
      this.userSubscriptionCache = null;
      this.lastUserSubscriptionCacheTime = 0;
      
      // Extract user subscription data from response
      let subscription: UserSubscription | null = null;
      
      if (response && typeof response === 'object') {
        if ('_response' in response) {
          // The response itself might be the subscription object with _response metadata
          if ('_id' in response && 'plan' in response && 'isActive' in response) {
            // This is directly the subscription object
            subscription = response as unknown as UserSubscription;
          } else {
            // Look for the subscription in numbered keys
            const numericKeys = Object.keys(response).filter(key => !isNaN(Number(key)));
            if (numericKeys.length > 0) {
              const responseObj = response as Record<string, any>;
              const firstKey = numericKeys[0];
              if (responseObj[firstKey]) {
                subscription = responseObj[firstKey] as UserSubscription;
              }
            }
          }
        } else if ('data' in response && response.data) {
          // Direct API format
          subscription = response.data as UserSubscription;
        }
      }
      
      console.log('SubscriptionService: Extracted subscription data:', subscription);
      return subscription;
    } catch (error) {
      console.error('SubscriptionService: Failed to upgrade plan:', error);
      throw error;
    }
  }
  
  // Helper function to calculate appropriate end date based on plan type
  calculateEndDate(startDate: Date, planType: string): Date {
    const endDate = new Date(startDate);
    
    if (planType === 'monthly') {
      // Add 1 month to the start date
      endDate.setMonth(endDate.getMonth() + 1);
    } else if (planType === 'yearly') {
      // Add 12 months (1 year) to the start date
      endDate.setFullYear(endDate.getFullYear() + 1);
    } else if (planType === 'quarterly') {
      // Add 3 months (1 quarter) to the start date
      endDate.setMonth(endDate.getMonth() + 3);
    }
    
    return endDate;
  }
  
  // Subscribe to a plan (handles both new purchase and upgrades)
  async subscribeToPlan(plan: SubscriptionPlan): Promise<UserSubscription | null> {
    try {
      // Get current subscription to determine if this is a new purchase or upgrade
      const currentSubscription = await this.getUserSubscription(true);
      
      const now = new Date();
      const startDate = now.toISOString();
      const endDate = this.calculateEndDate(now, plan.planType).toISOString();
      
      if (currentSubscription && currentSubscription.isActive) {
        // This is an upgrade scenario
        return this.upgradePlan(plan._id, startDate, endDate, plan.interviewMinutes);
      } else {
        // This is a new purchase scenario
        return this.purchasePlan(plan._id, startDate, endDate, plan.interviewMinutes);
      }
    } catch (error) {
      console.error('SubscriptionService: Failed to subscribe to plan:', error);
      throw error;
    }
  }
  
  // Clear all cache
  clearAllCache() {
    this.plansCache = null;
    this.userSubscriptionCache = null;
    this.lastCacheTime = {};
    this.lastUserSubscriptionCacheTime = 0;
    this.couponValidationCache = {};
    console.log('SubscriptionService: All cache cleared');
  }
  
  // Cancel subscription with optional cancellation reasons
  async cancelSubscription(cancellationRequest?: CancelSubscriptionRequest): Promise<boolean> {
    try {
      console.log('SubscriptionService: Cancelling subscription...', cancellationRequest);
      
      // Get current subscription to ensure we're modifying the correct one
      const currentSubscription = await this.getUserSubscription(true);
      
      if (!currentSubscription || !currentSubscription.isActive) {
        console.warn('SubscriptionService: No active subscription found to cancel');
        return false;
      }
      
      // Build payload with isActive: false and optional cancellation reasons
      const payload: any = {
        isActive: false
      };
      
      // Add cancellation reasons if provided
      if (cancellationRequest) {
        payload.cancellationReasons = cancellationRequest.reasons;
        payload.otherReason = cancellationRequest.otherReason;
        payload.feedbackOptional = cancellationRequest.feedbackOptional;
      }
      
      // Use PATCH to update the subscription with cancellation data
      const response = await apiClient.patch<ApiResponse<UserSubscription>>('/subscriptions/user', payload);
      console.log('SubscriptionService: Cancel subscription response:', response);
      
      // Clear the subscription cache to ensure fresh data on next fetch
      this.userSubscriptionCache = null;
      this.lastUserSubscriptionCacheTime = 0;
      
      // Check if the response indicates success
      if (response && typeof response === 'object') {
        if ('_response' in response) {
          const responseMetadata = (response as any)._response;
          if (responseMetadata && typeof responseMetadata === 'object' && 
              'statusCode' in responseMetadata && 
              responseMetadata.statusCode >= 200 && 
              responseMetadata.statusCode < 300) {
            console.log('SubscriptionService: Subscription successfully cancelled');
            return true;
          }
        } else if ('statusCode' in response && (response as any).statusCode >= 200 && (response as any).statusCode < 300) {
          console.log('SubscriptionService: Subscription successfully cancelled');
          return true;
        }
      }
      
      // If we couldn't confirm success but didn't throw an error, assume success
      console.log('SubscriptionService: Assuming subscription was cancelled successfully');
      return true;
    } catch (error) {
      console.error('SubscriptionService: Failed to cancel subscription:', error);
      throw error;
    }
  }
  
  /**
   * Validate a coupon code without requiring a specific plan
   * @param code Coupon code to validate
   * @returns Validation result with coupon details
   */
  async validateCoupon(code: string): Promise<CouponValidationResult> {
    try {
      // Normalize the code to ensure consistent caching
      const normalizedCode = code.trim().toUpperCase();
      console.log(`SubscriptionService: Validating coupon code ${normalizedCode}`);
      
      // Check cache first
      const cachedValidation = this.couponValidationCache[normalizedCode];
      const now = Date.now();
      
      if (cachedValidation && (now - cachedValidation.timestamp) < this.couponValidationCacheTimeout) {
        console.log('SubscriptionService: Returning cached coupon validation result');
        return cachedValidation.result;
      }
      
      const response = await apiClient.get(`/coupons/validate?couponCode=${normalizedCode}`, undefined, true);
      
      console.log('SubscriptionService: Coupon validation response:', response);
      
      // Direct check for the response structure we're getting
      if (response && typeof response === 'object' && 'isValid' in response) {
        // The response is directly what we want
        const responseObj = response as Record<string, any>;
        const couponData = responseObj.coupon || {};
        
        const result = {
          isValid: Boolean(responseObj.isValid),
          code: couponData.code || normalizedCode,
          couponId: couponData._id,
          couponType: couponData.couponType,
          value: couponData.discountValue,
          discountAmount: 0, // Will be calculated later with specific plan
          freeMinutes: couponData.freeMinutes || 0,
          originalPrice: 0, // Will be calculated later with specific plan
          discountedPrice: 0, // Will be calculated later with specific plan
          message: responseObj.message || 'Coupon applied successfully',
          
          // Include additional coupon details
          expiresAt: couponData.endDate,
          startDate: couponData.startDate,
          description: couponData.description,
          minPurchaseAmount: couponData.minPurchaseAmount,
          maxDiscountAmount: couponData.maxDiscountAmount,
          applicablePlans: couponData.applicablePlans || [],
          totalUsageLimit: couponData.totalUsageLimit,
          perUserLimit: couponData.perUserLimit,
          usageCount: couponData.usageCount || 0,
          requiresExistingSubscription: couponData.requiresExistingSubscription,
          razorpayOfferId: couponData.razorpayOfferId,
        };
        
        // Cache the result
        this.couponValidationCache[normalizedCode] = {
          timestamp: now,
          result
        };
        
        return result;
      }
      
      // If we get here, fall back to old logic
      let validationData: any = null;
      
      if (response && typeof response === 'object') {
        if ('_response' in response && '_response' in response && 
            response._response && typeof response._response === 'object' && 
            'statusCode' in response._response && response._response.statusCode === 200) {
          // Look for data in numeric keys
          const numericKeys = Object.keys(response).filter(key => !isNaN(Number(key)));
          if (numericKeys.length > 0) {
            const responseObj = response as Record<string, any>;
            validationData = responseObj[numericKeys[0]];
          } else {
            // If no numeric keys but we have a valid status, the response itself might be the data
            validationData = response;
          }
        } else if ('data' in response) {
          // Direct response format
          validationData = response.data;
        } else if ('statusCode' in response && (response as any).statusCode === 200) {
          // Another format where data might be directly in response
          validationData = response;
        }
      }
      
      if (validationData && validationData.isValid) {
        // Extract coupon details
        const couponData = validationData.coupon || {};
        
        const result = {
          isValid: true,
          couponId: validationData.couponId || couponData._id,
          code: couponData.code || validationData.code || normalizedCode,
          
          // Include detailed coupon information
          couponType: couponData.couponType || validationData.discountType,
          value: couponData.discountValue || validationData.value,
          discountAmount: validationData.discount || 0,
          freeMinutes: validationData.freeMinutes || couponData.freeMinutes || 0,
          originalPrice: validationData.originalPrice || 0,
          discountedPrice: validationData.discountedPrice || 0,
          
          // Include additional coupon details
          expiresAt: couponData.endDate || validationData.expiresAt,
          startDate: couponData.startDate,
          description: couponData.description,
          minPurchaseAmount: couponData.minPurchaseAmount,
          maxDiscountAmount: couponData.maxDiscountAmount,
          applicablePlans: couponData.applicablePlans || [],
          totalUsageLimit: couponData.totalUsageLimit,
          perUserLimit: couponData.perUserLimit,
          usageCount: couponData.usageCount || 0,
          requiresExistingSubscription: couponData.requiresExistingSubscription,
          razorpayOfferId: couponData.razorpayOfferId,
          
          message: validationData.message || 'Coupon applied successfully',
        };
        
        // Cache the result
        this.couponValidationCache[normalizedCode] = {
          timestamp: now,
          result
        };
        
        return result;
      }
      
      // If we reached here and have valid response but coupon is not valid
      if (validationData && !validationData.isValid) {
        const result = {
          isValid: false,
          discountAmount: 0,
          freeMinutes: 0,
          originalPrice: 0,
          discountedPrice: 0,
          message: validationData.message || 'Coupon is not valid',
        };
        
        // Cache invalid result too
        this.couponValidationCache[normalizedCode] = {
          timestamp: now,
          result
        };
        
        return result;
      }
      
      // Default error response
      const responseObj = response as Record<string, any>;
      const result = {
        isValid: false,
        discountAmount: 0,
        freeMinutes: 0,
        originalPrice: 0,
        discountedPrice: 0,
        message: responseObj && 'message' in responseObj ? responseObj.message : 'Invalid coupon code',
      };
      
      // Cache error result
      this.couponValidationCache[normalizedCode] = {
        timestamp: now,
        result
      };
      
      return result;
    } catch (error) {
      console.error('SubscriptionService: Error validating coupon:', error);
      let errorMessage = 'Failed to validate coupon';
      
      if ((error as any).response?.data?.message) {
        errorMessage = (error as any).response.data.message;
      } else if ((error as any).message) {
        errorMessage = (error as any).message;
      }
      
      return {
        isValid: false,
        discountAmount: 0,
        freeMinutes: 0,
        originalPrice: 0,
        discountedPrice: 0,
        message: errorMessage,
      };
    }
  }
  
  /**
   * Clear the coupon validation cache when needed
   * @param code Optional specific code to clear, or clear all if not provided
   */
  clearCouponCache(code?: string) {
    if (code) {
      delete this.couponValidationCache[code.trim().toUpperCase()];
    } else {
      this.couponValidationCache = {};
    }
  }
  
  /**
   * Redeem a coupon directly for free minutes without a purchase
   * @param couponCode Coupon code to redeem
   * @returns Result indicating success or failure
   */
  async redeemCouponForFreeMinutes(code: string): Promise<{
    success: boolean;
    message: string;
    freeMinutes?: number;
  }> {
    try {
      console.log(`SubscriptionService: Redeeming coupon code ${code} for free minutes`);
      
      const response = await apiClient.post('/coupons/redeem', { 
        couponCode: code 
      });
      
      console.log('SubscriptionService: Coupon redemption response:', response);
      
      // Handle response
      if (response && typeof response === 'object') {
        // Convert unknown response to proper typed response
        const typedResponse = response as any;
        return {
          success: Boolean(typedResponse.success),
          message: typedResponse.message || 'Coupon redeemed successfully',
          freeMinutes: typedResponse.freeMinutes
        };
      }
      
      return {
        success: false,
        message: 'Invalid response from server'
      };
    } catch (error) {
      console.error('SubscriptionService: Error redeeming coupon:', error);
      let errorMessage = 'Failed to redeem coupon';
      
      if ((error as any).response?.data?.message) {
        errorMessage = (error as any).response.data.message;
      } else if ((error as any).message) {
        errorMessage = (error as any).message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }
  
  /**
   * Calculate price with coupon applied
   * @param planId Plan ID to apply the coupon to
   * @param couponCode Coupon code to apply
   * @returns Calculated price details with discount and free minutes
   */
  async calculatePriceWithCoupon(planId: string, couponCode: string): Promise<{
    discountedPrice: number;
    originalPrice: number;
    discount: number;
    freeMinutes: number;
    couponId?: string;
  }> {
    try {
      // First validate the coupon to get its details
      const validationResult = await this.validateCoupon(couponCode);
      
      if (!validationResult.isValid) {
        throw new Error(validationResult.message);
      }
      
      // Get the plan details
      const plan = await this.findById(planId);
      if (!plan) {
        throw new Error('Plan not found');
      }
      
      // If we have applicable plans in the coupon details, check if this plan is applicable
      if (validationResult.applicablePlans && validationResult.applicablePlans.length > 0) {
        const isPlanApplicable = validationResult.applicablePlans.includes(planId);
        if (!isPlanApplicable) {
          throw new Error('Coupon is not applicable for this plan');
        }
      }
      
      // Calculate the discount based on coupon type
      let discount = 0;
      const originalPrice = plan.currentPrice;
      
      if (validationResult.couponType === 'percentage' && validationResult.value) {
        discount = (originalPrice * validationResult.value) / 100;
        
        // Apply max discount cap if applicable
        if (validationResult.maxDiscountAmount && discount > validationResult.maxDiscountAmount) {
          discount = validationResult.maxDiscountAmount;
        }
      } else if (validationResult.couponType === 'fixed_amount' && validationResult.value) {
        discount = validationResult.value;
        
        // Ensure discount doesn't exceed original price
        if (discount > originalPrice) {
          discount = originalPrice;
        }
      }
      
      // Calculate final discounted price
      const discountedPrice = Math.max(0, originalPrice - discount);
      
      return {
        discountedPrice,
        originalPrice,
        discount,
        freeMinutes: validationResult.freeMinutes || 0,
        couponId: validationResult.couponId
      };
    } catch (error) {
      console.error('SubscriptionService: Error calculating price with coupon:', error);
      throw error;
    }
  }
  
  /**
   * Format coupon benefit description based on discount type and value
   * @param couponDetails The coupon validation result
   * @returns A human-readable description of coupon benefits
   */
  formatCouponBenefits(couponDetails: CouponValidationResult): string {
    if (!couponDetails || !couponDetails.isValid) {
      return '';
    }
    
    const { couponType, value, discountAmount, freeMinutes } = couponDetails;
    
    if (couponType === 'percentage' && value) {
      return `${value}% discount (₹${discountAmount.toFixed(0)} off)`;
    } else if (couponType === 'fixed_amount' && discountAmount) {
      return `₹${discountAmount.toFixed(0)} discount`;
    } else if (couponType === 'free_minutes' && freeMinutes) {
      return `${freeMinutes} free interview minutes`;
    } else if (freeMinutes && discountAmount) {
      return `₹${discountAmount.toFixed(0)} off + ${freeMinutes} free minutes`;
    } else if (freeMinutes) {
      return `${freeMinutes} free interview minutes`;
    } else if (discountAmount) {
      return `₹${discountAmount.toFixed(0)} off`;
    }
    
    return 'Special offer applied';
  }
  
  /**
   * Apply a coupon directly to user account (for standalone coupons like free minutes)
   * @param code Coupon code to apply
   * @returns Result indicating success or failure
   */
  async applyCoupon(code: string): Promise<{success: boolean; message?: string}> {
    try {
      // Using the redeem endpoint for applying free minutes coupons
      return this.redeemCouponForFreeMinutes(code);
    } catch (error: any) {
      console.error('SubscriptionService: Error applying coupon:', error);
      
      let errorMessage = 'Failed to apply coupon';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Find a subscription plan by ID
   * @param id Plan ID to find
   * @returns Subscription plan or null if not found
   */
  async findById(id: string): Promise<SubscriptionPlan | null> {
    try {
      const plans = await this.getSubscriptionPlans();
      return plans.find(plan => plan._id === id) || null;
    } catch (error) {
      console.error(`SubscriptionService: Error finding plan ${id}:`, error);
      return null;
    }
  }
}

// Export an instance of the service
const subscriptionService = new SubscriptionService();
export default subscriptionService; 